/**
 * Who builds and who reviews, for team work — and the flow said plainly, since it is the default
 * whenever the person does not choose another: the Builder implements, the Reviewer tries to find
 * problems, the Builder fixes, the Reviewer re-checks and runs the tests, and it ends only when the
 * Reviewer is satisfied.
 *
 * Only teammates who switched on team work, with remote control on, can be picked; the others are
 * listed, disabled, with the reason — nobody's agent is reached without their say.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useActiveRepository } from '../lib/activeRepository'
import { useI18n } from '../lib/i18n'
import type { Teammate } from '../lib/types'

export interface TeamRoles {
  builder: string | null
  reviewer: string | null
}

export function TeamRolePicker({ value, onChange }: { value: TeamRoles; onChange: (roles: TeamRoles) => void }) {
  const { t } = useI18n()
  // Team work on a repository is for its team: the list follows the repository the app works on.
  const { active } = useActiveRepository()
  const { data: mates } = useQuery({ queryKey: ['teammates', active?.id ?? null], queryFn: () => api.workOrders.teammates(active?.id), retry: false })

  // Remote control on, or the desktop app open (it runs queued steps with any agent): either reaches them.
  const reachable = (m: Teammate) => m.remoteOn || Boolean(m.desktopOn)
  const why = (m: Teammate) =>
    m.isMe ? '' : !m.acceptsTeamWork ? ` — ${t('team.notAccepting')}` : !reachable(m) ? ` — ${t('team.remoteOff')}` : m.listening ? ` · ${t('team.listening')}` : m.desktopOn ? ` · ${t('team.desktopOn')}` : ''
  const can = (m: Teammate) => m.isMe || (m.acceptsTeamWork && reachable(m))
  const options = (placeholder: string) => (
    <>
      <option value="">{placeholder}</option>
      {(mates ?? []).map((m) => (
        <option key={m.developerId} value={m.developerId} disabled={!can(m)}>
          {m.isMe ? t('team.me', { name: m.name }) : m.name}
          {why(m)}
        </option>
      ))}
    </>
  )

  return (
    <div className="stack team-roles">
      <ol className="team-flow tiny">
        <li>{t('team.flow.build')}</li>
        <li>{t('team.flow.review')}</li>
        <li>{t('team.flow.fix')}</li>
        <li>{t('team.flow.recheck')}</li>
        <li>{t('team.flow.end')}</li>
      </ol>
      <div className="row row--wrap">
        <label className="field" style={{ flex: 1, minWidth: 180 }}>
          <span className="field__label">{t('team.builder')}</span>
          <select className="select" value={value.builder ?? ''} onChange={(e) => onChange({ ...value, builder: e.target.value || null })}>
            {options(t('team.builderMe'))}
          </select>
        </label>
        <label className="field" style={{ flex: 1, minWidth: 180 }}>
          <span className="field__label">{t('team.reviewer')}</span>
          <select className="select" value={value.reviewer ?? ''} onChange={(e) => onChange({ ...value, reviewer: e.target.value || null })}>
            {options(t('team.reviewerFresh'))}
          </select>
        </label>
      </div>
    </div>
  )
}
